import { Vector2 } from '../utils/Vector2';
import { TileGrid } from '../types/GameTypes';
import { Pathfinder } from '../maps/Pathfinder';

export type StructureType = 'wall' | 'tower' | 'totem';

export class MazeBuilder {
  private pathfinder: Pathfinder;

  constructor(pathfinder: Pathfinder) {
    this.pathfinder = pathfinder;
  }

  /**
   * Validate if a structure can be placed at the given tile
   */
  validatePlacement(
    tileX: number,
    tileY: number,
    structureType: StructureType,
    grid: TileGrid,
    spawns: Vector2[],
    goals: Vector2[]
  ): boolean {
    // Check bounds
    if (tileY < 0 || tileY >= grid.length || tileX < 0 || tileX >= grid[tileY].length) {
      return false;
    }

    const tile = grid[tileY][tileX];

    // Cannot place on spawn, goal, or border
    if (tile.type === 'spawn' || tile.type === 'goal' || tile.type === 'border') {
      return false;
    }

    // Cannot place on existing structure
    if (tile.type === 'wall' || tile.type === 'tower' || tile.type === 'totem') {
      return false;
    }

    // Check if placing next to spawn or goal (distance <= 1)
    const isNearSpawnOrGoal = spawns.some(spawn => 
      this.manhattanDistance(tileX, tileY, spawn.x, spawn.y) <= 1
    ) || goals.some(goal => 
      this.manhattanDistance(tileX, tileY, goal.x, goal.y) <= 1
    );

    // For walls, always check path integrity
    if (structureType === 'wall') {
      // Temporarily place wall
      const originalType = tile.type;
      tile.type = 'wall';

      // Check if at least one path exists from each spawn to each goal
      let pathExists = true;
      for (const spawn of spawns) {
        for (const goal of goals) {
          if (!this.pathfinder.checkPathExists(spawn, goal, grid)) {
            pathExists = false;
            break;
          }
        }
        if (!pathExists) break;
      }

      // Restore original type
      tile.type = originalType;

      return pathExists;
    }

    // For towers and totems near spawn/goal, verify path still exists
    // (Even though they don't block paths, we want to ensure at least one tile remains walkable)
    if (isNearSpawnOrGoal) {
      // Temporarily place structure to check if it would block all paths
      const originalType = tile.type;
      tile.type = structureType === 'tower' ? 'tower' : 'totem';

      // Check if at least one path exists from each spawn to each goal
      let pathExists = true;
      for (const spawn of spawns) {
        for (const goal of goals) {
          if (!this.pathfinder.checkPathExists(spawn, goal, grid)) {
            pathExists = false;
            break;
          }
        }
        if (!pathExists) break;
      }

      // Restore original type
      tile.type = originalType;

      return pathExists;
    }

    // Towers and totems don't block paths when not near spawn/goal
    return true;
  }

  /**
   * Place a structure at the given tile
   */
  placeStructure(
    tileX: number,
    tileY: number,
    structureType: StructureType,
    grid: TileGrid
  ): boolean {
    if (tileY < 0 || tileY >= grid.length || tileX < 0 || tileX >= grid[tileY].length) {
      return false;
    }

    const tile = grid[tileY][tileX];
    
    if (structureType === 'wall') {
      tile.type = 'wall';
    } else if (structureType === 'tower') {
      tile.type = 'tower';
    } else if (structureType === 'totem') {
      tile.type = 'totem';
    } else {
      return false;
    }

    // Clear path cache when maze changes
    this.pathfinder.clearCache();

    return true;
  }

  /**
   * Remove a structure (destroy)
   */
  removeStructure(tileX: number, tileY: number, grid: TileGrid): boolean {
    if (tileY < 0 || tileY >= grid.length || tileX < 0 || tileX >= grid[tileY].length) {
      return false;
    }

    const tile = grid[tileY][tileX];
    
    if (tile.type === 'wall' || tile.type === 'tower' || tile.type === 'totem') {
      tile.type = 'empty';
      this.pathfinder.clearCache();
      return true;
    }

    return false;
  }

  /**
   * Check path integrity (ensures at least one path exists)
   */
  checkPathIntegrity(grid: TileGrid, spawns: Vector2[], goals: Vector2[]): boolean {
    for (const spawn of spawns) {
      for (const goal of goals) {
        if (!this.pathfinder.checkPathExists(spawn, goal, grid)) {
          return false;
        }
      }
    }
    return true;
  }

  private manhattanDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.abs(x1 - x2) + Math.abs(y1 - y2);
  }
}

